/**
@format
@flow
*/
import React from 'react';
import { VideoPlayer as KrispyVideo } from '@krispy/media/web';

type Props = $ReadOnly<{|
  /**
   * Show controls
   */
  controls?: boolean,

  /**
   * URL for video to be used.
   */
  src: string,

  /**
   * Alt text for video.
   */
  alt?: string,

  /**
   * Set video width.
   * Defaults to 400
   * NOTE: do not user percent
   */
  width?: string | number,

  /**
   * Set video height.
   * Defaults to 400
   * NOTE: do not user percent
   */
  height?: string | number,
  /**
   * Options to preload
   * Default is 'none'
   * If autoplay is true, this prop will be
   * disregarded
   */
  preload?: 'none' | 'auto' | 'metadata',
  autoplay?: boolean,
  paused?: boolean,
  loop?: boolean,
  muted?: boolean,
  fullscreen?: boolean,
  onPlay?: () => void,
  onPause?: () => void,
  canPlay?: () => void,
  canPlayThrough?: () => void,
  onTimeUpdate?: () => void,
  onEnd?: () => void,
  getRef?: any,
  onSeekEnd?: () => void,
  onSeek?: () => void,
  onLoadedMetadata?: () => void,
  /**
   * Fired when first frame of video is loaded
   * Used to trigger overlay in VideoSquare and CardVideo
   */
  onLoadedData?: () => void,
|}>;

const VideoPlayer = (props: Props): React$Node => {
  const {
    src,
    alt,
    width,
    height,
    controls,
    autoplay,
    loop,
    preload,
    muted,
    fullscreen,
    paused,
    onPlay,
    onPause,
    canPlay,
    canPlayThrough,
    onTimeUpdate,
    onEnd,
    getRef,
    onSeekEnd,
    onSeek,
    onLoadedMetadata,
    onLoadedData,
    ...rest
  } = props;

  const _getRef = () => {
    if (getRef) { return getRef; }
    return null;
  };

  return (
    <KrispyVideo
      {...rest}
      preload={preload}
      controls={controls}
      width={width}
      height={height}
      alt={alt}
      src={src}
      getRef={_getRef()}
      autoplay={autoplay}
      paused={paused}
      loop={loop}
      muted={muted}
      fullscreen={fullscreen}
      onPlay={onPlay}
      onPause={onPause}
      onCanPlay={canPlay}
      onCanPlayThrough={canPlayThrough}
      onTimeUpdate={onTimeUpdate}
      onLoadedMetadata={onLoadedMetadata}
      onLoadedData={onLoadedData}
      onEnd={onEnd}
      onSeekEnd={onSeekEnd}
      onSeek={onSeek}
      playsInline
    >
      This browser does not support the HTML5 video element.
    </KrispyVideo>
  );
};

VideoPlayer.defaultProps = {
  controls: true,
  width: '400',
  height: 'auto',
  alt: 'ziynx video',
  preload: 'none',
  autoplay: false,
  paused: undefined,
  loop: false,
  muted: false,
  fullscreen: false,
  onPlay: undefined,
  onPause: undefined,
  canPlay: undefined,
  canPlayThrough: undefined,
  onTimeUpdate: undefined,
  onEnd: undefined,
  onSeekEnd: undefined,
  onLoadedMetadata: undefined,
};

export default VideoPlayer;
